// App.jsx
import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Button,
  Slider,
  Typography,
  Stack,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Brush as BrushIcon,
  DeleteOutline as EraseIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  FileUpload as UploadIcon,
  FileDownload as DownloadIcon,
  Image as ImageIcon,
  Clear as ClearIcon
} from '@mui/icons-material';

const AdvancedInpaintEditor = () => {
  const canvasRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const maskFileInputRef = useRef(null);
  const containerRef = useRef(null);
  
  const [isDrawing, setIsDrawing] = useState(false);
  const [brushSize, setBrushSize] = useState(20);
  const [isEraseMode, setIsEraseMode] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });
  const lastPosRef = useRef({ x: 0, y: 0 });

 // 線の補間処理を改善
 const getLinePoints = (x1, y1, x2, y2) => {
    const points = [];
    const distance = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
    
    // 距離に応じて補間点の数を調整
    const minPoints = Math.max(Math.ceil(distance / 2), 2);
    const steps = Math.max(minPoints, Math.floor(distance / (brushSize / 8)));
    
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      // ベジェ曲線の中間点を使用して滑らかな線を生成
      points.push({
        x: x1 + (x2 - x1) * t,
        y: y1 + (y2 - y1) * t
      });
    }
    
    return points;
  };

  const initializeCanvas = (width, height) => {
    const canvas = canvasRef.current;
    const overlay = overlayCanvasRef.current;
    const container = containerRef.current;

    canvas.width = width;
    canvas.height = height;
    overlay.width = width;
    overlay.height = height;

    const aspectRatio = width / height;
    const maxWidth = 800;
    const maxHeight = 600;
    let displayWidth = width;
    let displayHeight = height;

    if (width > maxWidth || height > maxHeight) {
      if (aspectRatio > maxWidth / maxHeight) {
        displayWidth = maxWidth;
        displayHeight = displayWidth / aspectRatio;
      } else {
        displayHeight = maxHeight;
        displayWidth = displayHeight * aspectRatio;
      }
    }

    container.style.width = `${displayWidth}px`;
    container.style.height = `${displayHeight}px`;
    setCanvasSize({ width: displayWidth, height: displayHeight });

    const newHistory = [overlay.toDataURL()];
    setHistory(newHistory);
    setHistoryIndex(0);
  };

  // マウス座標の変換を改善
  const getCanvasCoordinates = (e) => {
    const overlay = overlayCanvasRef.current;
    const rect = overlay.getBoundingClientRect();
    const scaleX = overlay.width / rect.width;
    const scaleY = overlay.height / rect.height;
    
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const draw = (e) => {
    if (!isDrawing) return;

    const overlay = overlayCanvasRef.current;
    const ctx = overlay.getContext('2d');
    const { x: currentX, y: currentY } = getCanvasCoordinates(e);
    const { x: lastX, y: lastY } = lastPosRef.current;

    // 描画設定
    ctx.globalCompositeOperation = isEraseMode ? 'destination-out' : 'source-over';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // パスの開始
    //ctx.beginPath();
    ctx.moveTo(lastX, lastY);

    const points = getLinePoints(lastX, lastY, currentX, currentY);
    
    // 滑らかな線を描画
    if (points.length > 2) {
      //ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      
      for (let i = 1; i < points.length - 2; i++) {
        const xc = (points[i].x + points[i + 1].x) / 2;
        const yc = (points[i].y + points[i + 1].y) / 2;
        ctx.quadraticCurveTo(points[i].x, points[i].y, xc, yc);
      }
      
      ctx.quadraticCurveTo(
        points[points.length - 2].x,
        points[points.length - 2].y,
        points[points.length - 1].x,
        points[points.length - 1].y
      );
    } else {
      ctx.lineTo(currentX, currentY);
    }

    // 線を描画
    ctx.strokeStyle = isEraseMode ? 'rgba(0, 0, 0, 1)' : 'rgba(255, 255, 255, 0.5)';
    ctx.lineWidth = brushSize;
    ctx.stroke();

    // 端点を円で描画してギャップを埋める
    //ctx.beginPath();
    //ctx.arc(currentX, currentY, brushSize / 2, 0, Math.PI * 2);
    ctx.fillStyle = isEraseMode ? 'rgba(0, 0, 0, 1)' : 'rgba(255, 255, 255, 0.5)';
    ctx.fill();

    lastPosRef.current = { x: currentX, y: currentY };
  };

  // 描画開始時の処理を改善
  const startDrawing = (e) => {
    setIsDrawing(true);
    const coords = getCanvasCoordinates(e);
    lastPosRef.current = coords;

    // 開始点を描画
    const overlay = overlayCanvasRef.current;
    const ctx = overlay.getContext('2d');
    ctx.globalCompositeOperation = isEraseMode ? 'destination-out' : 'source-over';
    ctx.beginPath();
    //ctx.arc(coords.x, coords.y, brushSize / 2, 0, Math.PI * 2);
    ctx.fillStyle = isEraseMode ? 'rgba(0, 0, 0, 1)' : 'rgba(255, 255, 255, 0.5)';
    ctx.fill();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      addToHistory();
    }
  };

  const addToHistory = () => {
    const overlay = overlayCanvasRef.current;
    const newState = overlay.toDataURL();
    
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newState);
    
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const restoreFromHistory = (state) => {
    const overlay = overlayCanvasRef.current;
    const ctx = overlay.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      ctx.clearRect(0, 0, overlay.width, overlay.height);
      ctx.drawImage(img, 0, 0);
    };
    img.src = state;
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      restoreFromHistory(history[historyIndex - 1]);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      restoreFromHistory(history[historyIndex + 1]);
    }
  };

  const clearMask = () => {
    const overlay = overlayCanvasRef.current;
    const ctx = overlay.getContext('2d');
    ctx.clearRect(0, 0, overlay.width, overlay.height);
    addToHistory();
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          initializeCanvas(img.width, img.height);
          const ctx = canvasRef.current.getContext('2d');
          ctx.drawImage(img, 0, 0);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleMaskUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const overlay = overlayCanvasRef.current;
          const ctx = overlay.getContext('2d');
          ctx.drawImage(img, 0, 0, overlay.width, overlay.height);
          addToHistory();
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleMaskDownload = () => {
    const overlay = overlayCanvasRef.current;
    const link = document.createElement('a');
    link.download = 'mask.png';
    link.href = overlay.toDataURL();
    link.click();
  };

  useEffect(() => {
    initializeCanvas(800, 600);
  }, []);

  return (
    <Paper elevation={3} sx={{ maxWidth: '4xl', p: 2 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Inpaint Mask Editor
        </Typography>
      </Box>

      <Box
        ref={containerRef}
        sx={{
          position: 'relative',
          width: canvasSize.width,
          height: canvasSize.height,
          mb: 2,
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <canvas
          ref={canvasRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%'
          }}
        />
        <canvas
          ref={overlayCanvasRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'all'
          }}
          onMouseDown={startDrawing}
          onMouseUp={stopDrawing}
          onMouseOut={stopDrawing}
          onMouseMove={draw}
        />
      </Box>

      <Divider sx={{ my: 2 }} />

      <Stack spacing={2}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Stack direction="row" spacing={1}>
            <Tooltip title="マスク描画">
              <IconButton
                color={isEraseMode ? 'default' : 'primary'}
                onClick={() => setIsEraseMode(false)}
              >
                <BrushIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="消しゴム">
              <IconButton
                color={isEraseMode ? 'primary' : 'default'}
                onClick={() => setIsEraseMode(true)}
              >
                <EraseIcon />
              </IconButton>
            </Tooltip>
          </Stack>

          <Divider orientation="vertical" flexItem />

          <Stack direction="row" spacing={1}>
            <Tooltip title="元に戻す">
              <span>
                <IconButton
                  onClick={handleUndo}
                  disabled={historyIndex <= 0}
                >
                  <UndoIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="やり直し">
              <span>
                <IconButton
                  onClick={handleRedo}
                  disabled={historyIndex >= history.length - 1}
                >
                  <RedoIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          <Divider orientation="vertical" flexItem />

          <Stack direction="row" spacing={1}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageUpload}
              accept="image/*"
              style={{ display: 'none' }}
            />
            <Tooltip title="画像読込">
              <IconButton onClick={() => fileInputRef.current.click()}>
                <ImageIcon />
              </IconButton>
            </Tooltip>

            <input
              type="file"
              ref={maskFileInputRef}
              onChange={handleMaskUpload}
              accept="image/*"
              style={{ display: 'none' }}
            />
            <Tooltip title="マスク読込">
              <IconButton onClick={() => maskFileInputRef.current.click()}>
                <UploadIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="マスク保存">
              <IconButton onClick={handleMaskDownload}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </Stack>

          <Divider orientation="vertical" flexItem />

          <Tooltip title="マスクをクリア">
            <IconButton onClick={clearMask} color="error">
              <ClearIcon />
            </IconButton>
          </Tooltip>
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center">
          <Typography>ブラシサイズ:</Typography>
          <Slider
            value={brushSize}
            onChange={(e, newValue) => setBrushSize(newValue)}
            min={1}
            max={50}
            valueLabelDisplay="auto"
            sx={{ width: 200 }}
          />
        </Stack>
      </Stack>
    </Paper>
  );
};

export default AdvancedInpaintEditor;